import { AxiosResponse } from 'axios';
import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import {
  getJobsFromAdvancedQuery,
  getCandidateStatsByJobIdsPostgres,
  IJob,
  IJobSearchFilterDTO,
  getJobsWorkAssignmentData,
  getPotentialRevenueForJobs,
  getInfoTemplateMappingsByJobIds,
  getEvaluationTemplateMappingsByJobIds,
} from 'wisestep-api';
import { IInformationTemplate, IEvaluationTemplate } from 'wisestep-base';

import { paginationSettingsStore } from '../../../../utils/PaginationSettingStore';
import { candidateStatsDefaultQuery, jobCrmStatesCount } from '../JObSearchFilterDefaultQuery';

export interface PotentialRevenueDTO {
  id: string;
  potentialRevenue: number;
  potentialRevenueCurrency: string;
  potentialRevenueTimeSpan: string | null;
}

export interface IListingJobsState {
  jobListArr: (IJob & { isSelected: boolean })[];
  candidateStatsES: jobCrmStatesCount[];
  totalJobs: number;
  workAssignmentData?: Record<string, any>;
  potentialRevenueData?: PotentialRevenueDTO[];
  informationTemplateMappings?: Record<string, IInformationTemplate[]>;
  evaluationTemplateMappings?: Record<string, IEvaluationTemplate[]>;
}

const useListingJobs = (
  query: IJobSearchFilterDTO,
  setLoader: Dispatch<SetStateAction<boolean>>
): IListingJobsState => {
  const paginationSettings = paginationSettingsStore((state) => state.getPaginationSettings());
  query.size = Number(paginationSettings?.jobsListPagination ?? 20);
  const [state, setState] = useState<IListingJobsState>({
    jobListArr: [],
    candidateStatsES: [],
    totalJobs: 0,
  });

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoader(true);
        const response = await getJobsFromAdvancedQuery({ ...query });
        const jobs: IListingJobsState['jobListArr'] = response.data;
        if (jobs) {
          const q = { ...candidateStatsDefaultQuery };
          const jobIds = jobs.filter((job) => job.id).map((job) => job.id as string);
          q.jobIds = jobIds.toString();

          const settledResponses = await Promise.allSettled([
            getCandidateStatsByJobIdsPostgres(q),
            getJobsWorkAssignmentData(jobIds),
            getPotentialRevenueForJobs(jobIds),
            getInfoTemplateMappingsByJobIds(jobIds),
            getEvaluationTemplateMappingsByJobIds(jobIds),
          ]);

          const getResponseData = <T = any>(
            settledResult: PromiseSettledResult<AxiosResponse>
          ): T | undefined => {
            if (settledResult.status == 'fulfilled') return settledResult.value.data;
          };

          setState((prev) => ({
            ...prev.workAssignmentData,
            jobListArr: jobs,
            totalJobs: Number(response.headers['x-total-count']),
            candidateStatsES: getResponseData(settledResponses[0]) ?? [],
            workAssignmentData: getResponseData(settledResponses[1]) ?? {},
            potentialRevenueData: getResponseData(settledResponses[2]) ?? [],
            informationTemplateMappings: getResponseData(settledResponses[3]) ?? {},
            evaluationTemplateMappings: getResponseData(settledResponses[4]) ?? {},
          }));
        }
      } catch (e) {
        console.error('Error fetching Advanced query job data', e);
      } finally {
        setLoader(false);
      }
    };

    fetchJobs();
  }, [query, setLoader]);

  return state;
};

export default useListingJobs;
